import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  ShamMoney01Icon,
  ShamUser03Icon,
  ShamEdit01Icon,
  ShamLockPasswordStrokeIcon,
  ShamLocationAddressIcon,
  ShamPackageIcon,
  ShamShoppingCartCheckIcon,
  ShamTicket01StrokeIcon,
  ShamBellIcon,
  ShamWallet02Icon,
} from 'sham-components/icons';
import { AccountApi } from '@/app/services/account.api';
import { TranslateModule } from '@ngx-translate/core';
import { CustomerNotificationsStore } from '@/app/services/customer-notifications.store';

@Component({
  selector: 'app-user-profile-page',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    ShamUser03Icon,
    RouterOutlet,
    RouterLinkActive,
    ShamEdit01Icon,
    ShamLockPasswordStrokeIcon,
    ShamLocationAddressIcon,
    ShamShoppingCartCheckIcon,
    ShamMoney01Icon,
    ShamPackageIcon,
    TranslateModule,
    ShamTicket01StrokeIcon,
    ShamBellIcon,
    ShamWallet02Icon,
  ],
  templateUrl: './user-profile-page.html',
  styleUrl: './user-profile-page.css',
})
export class UserProfilePage {
  isLoading = signal(false);
  private readonly accountApi = inject(AccountApi);
  private readonly notificationsStore = inject(CustomerNotificationsStore);
  readonly unreadNotificationsCount = this.notificationsStore.unreadCount;

  user = signal({
    name: '',
    email: '',
    initials: '',
    profilePhotoUrl: null as string | null,
    roles: [] as string[],
  });

  ngOnInit(): void {
    this.loadUser();
    this.notificationsStore.ensureReady();
    this.accountApi.refreshUser$.subscribe(() => {
      this.loadUser();
    });
  }

  loadUser() {
    this.isLoading.set(true);

    this.accountApi.getMe().subscribe({
      next: (response) => {
        this.user.set({
          name: response.fullName ?? '',
          email: response.email,
          initials: this.getInitials(response.fullName),
          profilePhotoUrl: response.profilePhotoUrl,
          roles: response.roles ?? [],
        });

        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  private getInitials(name: string | null): string {
    if (!name) return '';

    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
  }

  readonly menuItems = computed(() => {
    const items = [
      { label: 'UserMenu.Profile', route: 'my-profile', icon: 'user' },
      { label: 'UserMenu.ChangePassword', route: 'change-password', icon: 'lock' },
      { label: 'UserMenu.Address', route: 'addresses', icon: 'location' },
      { label: 'UserMenu.Purchases', route: 'purchases', icon: 'cart' },
      // { label: 'UserMenu.BanksCards', route: 'banks-cards', icon: 'money' },
      { label: 'UserMenu.Orders', route: 'orders', icon: 'package' },
      { label: 'UserMenu.MyReviews', route: 'my-reviews', icon: 'package' },
      { label: 'UserMenu.Tickets', route: 'tickets', icon: 'ticket' },
      {
        label: 'UserMenu.Notifications',
        route: 'notifications',
        icon: 'notifications',
        badgeCount: this.unreadNotificationsCount(),
      },
    ];

    if (this.user().roles.includes('Customer')) {
      items.push({
        label: 'UserMenu.Wallet',
        route: 'wallet',
        icon: 'wallet',
      });
    }

    return items;
  });
}
